/**
 * Modal de janela de acesso restrito (RN-012).
 *
 * A lista de usuários mora AQUI, e não na tela, porque é aqui que se decide quem passa. As
 * colunas de senha e último acesso ficam ao lado da decisão: dá para ver quem ainda não criou a
 * senha antes de escolher liberá-lo.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { colors, inputs } from "../../theme/designSystem";
import SavingOverlay from "../SavingOverlay";
import { searchFieldSx } from "../../theme/filterStyles";
import type {
  AccessRestrictionCandidate,
  AccessRestrictionItem,
} from "../../services/accessRestrictionService";

const MESSAGE_MAX = 500;
const SEARCH_MAX = 100;

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * `datetime-local` fala o fuso do NAVEGADOR e o servidor guarda UTC. A conversão usa o fuso de
 * quem configura, não o da entidade — exato quando são o mesmo, deslocado quando não. Registrado
 * como item 48 da lista de ataque.
 */
const toDateInput = (isoUtc: string | null): string => {
  if (!isoUtc) return "";
  const d = new Date(isoUtc);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toTimeInput = (isoUtc: string | null): string => {
  if (!isoUtc) return "";
  const d = new Date(isoUtc);
  if (isNaN(d.getTime())) return "";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Data + hora locais (dos inputs) para um instante ISO em UTC. */
const toIsoUtc = (date: string, time: string): string | null => {
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}`);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * Mesmo visual dos campos de data/hora do modal de agendamento (AppointmentModal): o design
 * system dita altura, fonte, borda e foco. Copiado, não reinventado.
 */
const dateTimeFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: inputs.default.height,
    fontSize: inputs.default.fontSize,
    "& fieldset": {
      borderColor: inputs.default.borderColor,
      legend: { maxWidth: inputs.default.legendMaxWidth },
    },
    "&:hover fieldset": { borderColor: inputs.default.hover.borderColor },
    "&.Mui-focused fieldset": {
      borderColor: inputs.default.focus.borderColor,
      boxShadow: inputs.default.focus.boxShadow,
    },
  },
};

const dateTimeLabelProps = {
  shrink: true,
  sx: {
    fontSize: inputs.default.labelFontSize,
    color: inputs.default.labelColor,
    backgroundColor: inputs.default.labelBackground,
    padding: inputs.default.labelPadding,
    "&.Mui-focused": { color: inputs.default.focus.labelColor },
  },
};

export const formatMoment = (isoUtc: string | null): string => {
  if (!isoUtc) return "—";
  const d = new Date(isoUtc);
  if (isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

interface Props {
  open: boolean;
  janela: AccessRestrictionItem | null;
  candidates: AccessRestrictionCandidate[];
  currentUserId: string;
  saving: boolean;
  onClose: () => void;
  onSave: (payload: {
    startsAt: string;
    endsAt: string;
    message: string;
    allowedUserIds: string[];
  }) => void;
}

const AccessRestrictionModal: React.FC<Props> = ({
  open, janela, candidates, currentUserId, saving, onClose, onSave,
}) => {
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [message, setMessage] = useState("");
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!open) return;
    // Início pré-preenchido com AGORA: todo campo é obrigatório (Hilton, 03/09) e o caso comum —
    // "fechar já" — continua a um clique. Para agendar, basta trocar a data.
    const inicio = janela ? janela.startsAt : new Date().toISOString();
    setStartDate(toDateInput(inicio));
    setStartTime(toTimeInput(inicio));
    setEndDate(toDateInput(janela?.endsAt ?? null));
    setEndTime(toTimeInput(janela?.endsAt ?? null));
    setMessage(janela?.message ?? "");
    // Quem cria já entra liberado, e o visto dele fica TRAVADO: sem isso uma janela salva sem o
    // próprio administrador fecharia o sistema para todos, sem ninguém dentro para reabrir.
    const fixos = [
      ...(currentUserId ? [currentUserId] : []),
      ...candidates.filter((c) => c.isServiceAccount).map((c) => c.id),
    ];
    setAllowed(new Set([...(janela ? janela.allowedUserIds : []), ...fixos]));
    setSearch("");
    setErro("");
  }, [open, janela, currentUserId, candidates]);

  const filtrados = useMemo(() => {
    const termo = normalize(search.trim());
    if (!termo) return candidates;
    return candidates.filter((c) => normalize(`${c.name} ${c.email}`).includes(termo));
  }, [candidates, search]);

  // Travados: o próprio usuário e as contas de serviço. Não se desmarcam nem pela linha.
  const travado = (c: AccessRestrictionCandidate) => c.id === currentUserId || c.isServiceAccount;

  const alternar = (id: string) =>
    setAllowed((anterior) => {
      const proximo = new Set(anterior);
      if (proximo.has(id)) {
        proximo.delete(id);
      } else {
        proximo.add(id);
      }
      return proximo;
    });

  const salvar = () => {
    const fim = toIsoUtc(endDate, endTime);
    const inicio = toIsoUtc(startDate, startTime);

    if (!inicio) {
      setErro("Informe a partir de quando o sistema fica bloqueado.");
      return;
    }
    if (!fim) {
      setErro("Informe até quando o sistema fica bloqueado.");
      return;
    }
    if (!message.trim()) {
      setErro("Escreva a mensagem que o usuário barrado vai ler na tela de login.");
      return;
    }
    if (new Date(fim) <= new Date()) {
      setErro("O fim precisa estar no futuro — uma janela vencida não bloqueia nada.");
      return;
    }
    if (new Date(inicio) >= new Date(fim)) {
      setErro("O início precisa ser anterior ao fim.");
      return;
    }
    if (allowed.size === 0) {
      setErro("Marque ao menos um usuário liberado.");
      return;
    }
    if (currentUserId && !allowed.has(currentUserId)) {
      setErro("Inclua você mesmo: sem isso você não conseguiria entrar para encerrar a janela.");
      return;
    }

    setErro("");
    onSave({
      startsAt: inicio,
      endsAt: fim,
      message: message.trim(),
      allowedUserIds: Array.from(allowed),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
    >
      <SavingOverlay open={saving} />
      {/* Faixa ciano + X: moldura copiada do modal de Salas, o canônico do sistema. */}
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {janela ? "Editar janela de bloqueio" : "Nova janela de bloqueio"}
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
        {/* Data e hora em campos separados, como no modal de agendamento: "dd/mm/aaaa" + ícone
            não cabem junto com a hora num campo só. Erro pela borda, sem asterisco. */}
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Bloquear a partir de</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1.5fr 1fr" }, gap: "1rem", mb: 2 }}>
          <TextField
            label="Data"
            type="date"
            value={startDate}
            error={!startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={dateTimeLabelProps}
            sx={dateTimeFieldSx}
          />
          <TextField
            label="Horário"
            type="time"
            value={startTime}
            error={!startTime}
            onChange={(e) => setStartTime(e.target.value)}
            InputLabelProps={dateTimeLabelProps}
            sx={dateTimeFieldSx}
          />
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Liberar o sistema em</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1.5fr 1fr" }, gap: "1rem", mb: 2 }}>
          <TextField
            label="Data"
            type="date"
            value={endDate}
            error={!endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={dateTimeLabelProps}
            sx={dateTimeFieldSx}
          />
          <TextField
            label="Horário"
            type="time"
            value={endTime}
            error={!endTime}
            onChange={(e) => setEndTime(e.target.value)}
            InputLabelProps={dateTimeLabelProps}
            sx={dateTimeFieldSx}
          />
        </Box>

        <TextField
          size="small"
          label="Mensagem na tela de login"
          placeholder="Seu acesso está confirmado. O sistema abre terça, às 8h."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          error={!message.trim()}
          fullWidth
          multiline
          minRows={2}
          // Sem searchFieldSx aqui: ele fixa a altura do campo, e num multiline o texto e o
          // placeholder escapam por cima do que está em volta (gotcha registrado).
          sx={{
            mb: 2,
            backgroundColor: colors.white,
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: colors.border },
              "&:hover fieldset": { borderColor: colors.border },
              "&.Mui-focused fieldset": { borderColor: colors.primary },
            },
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: MESSAGE_MAX }}
        />

        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
          Quem continua entrando ({allowed.size} de {candidates.length})
        </Typography>

        <TextField
          size="small"
          label="Busca por nome ou email"
          placeholder="Buscar usuário"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ ...searchFieldSx, mb: 1 }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: SEARCH_MAX }}
        />

        <Box className="admin-plans-table" sx={{ maxHeight: 300, overflowY: "auto" }}>
          <Box className="c4u-table__header">
            <Box className="admin-plans-header-cell" sx={{ flex: "1 1 200px", minWidth: 160 }}>
              Usuário
            </Box>
            <Box className="admin-plans-header-cell" sx={{ flex: "0 0 96px" }}>
              Senha
            </Box>
            <Box className="admin-plans-header-cell" sx={{ flex: "0 0 72px", justifyContent: "flex-end" }}>
              Libera
            </Box>
          </Box>
          <Box className="admin-plans-table-body">
            {filtrados.map((c) => (
              <Box
                key={c.id}
                className="c4u-table__row"
                onClick={() => !travado(c) && alternar(c.id)}
                sx={{ cursor: travado(c) ? "default" : "pointer" }}
              >
                <Box
                  className="admin-plans-cell admin-plans-name"
                  sx={{ flex: "1 1 200px", minWidth: 160, flexWrap: "wrap" }}
                  data-label="Usuário"
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {c.name}
                    {c.id === currentUserId && (
                      <Typography component="span" sx={{ fontSize: 11, color: colors.primary, ml: 1 }}>
                        (você — sempre liberado)
                      </Typography>
                    )}
                    {c.isServiceAccount && (
                      <Typography component="span" sx={{ fontSize: 11, color: colors.textSecondary, ml: 1 }}>
                        (monitoramento — sempre liberado)
                      </Typography>
                    )}
                  </Typography>
                  {/* Último acesso como linha secundária: é registrado no login, não na última
                      ação — cabe como contexto, não como coluna. */}
                  <Typography sx={{ fontSize: 11, color: "#adb5bd", width: "100%" }}>
                    {c.email && c.email !== c.name ? `${c.email} · ` : ""}
                    {c.lastAccessAt ? `último login ${formatMoment(c.lastAccessAt)}` : "nunca entrou"}
                  </Typography>
                </Box>
                <Box className="admin-plans-cell" sx={{ flex: "0 0 96px" }} data-label="Senha">
                  <Typography sx={{ fontSize: 12, color: c.passwordPending ? "#dc3545" : colors.textSecondary }}>
                    {c.passwordPending ? "Não criada" : "Criada"}
                  </Typography>
                </Box>
                <Box className="admin-plans-cell admin-plans-actions" sx={{ flex: "0 0 72px" }} data-label="Libera">
                  <Checkbox
                    checked={allowed.has(c.id)}
                    disabled={travado(c)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => alternar(c.id)}
                    size="small"
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {erro && <Typography sx={{ fontSize: 13, color: "#dc3545", mt: 2 }}>{erro}</Typography>}
      </DialogContent>

      <DialogActions
        sx={{
          p: "1rem 1.5rem",
          gap: 1,
          justifyContent: "flex-end",
          "& .MuiButton-root": { flexShrink: 0, minHeight: 44 },
        }}
      >
        <Button onClick={onClose} disabled={saving} variant="outlined" sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          onClick={salvar}
          disabled={saving}
          variant="contained"
          sx={{
            backgroundColor: colors.primary,
            textTransform: "none",
            "&.Mui-disabled": { backgroundColor: colors.primary, opacity: 0.7, color: "white" },
          }}
        >
          {saving ? "Processando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessRestrictionModal;
